import React, { useMemo, useState } from 'react';
import Card from './components/Card';
import ReferencePagesBanner from './components/ReferencePagesBanner';
import ReferencePageBadge from './components/ReferencePageBadge';
import {
  isReferencePage,
  getReferencePagesList,
  sortRowsWithReferenceFirst,
  REFERENCE_ROW_CLASS,
} from './utils/referencePages';
import Loader from './components/Loader';
import Badge from './components/Badge';
import Table from './components/Table';
import Tooltip from './components/Tooltip';
import Button from './components/Button';
import SharedScorePill from './components/ScorePill';
import AuditEmptyState from './components/AuditEmptyState';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { Info, Download, AlertTriangle, Eye, XCircle } from 'lucide-react';
import { usePerformanceAudits } from './hooks/usePerformanceAudits';
import { getScoreLabelText } from './utils/scoreLabels';
import useProjectScores from './hooks/useProjectScores';
import { getScoreColor } from './utils/scoreCalculations';

// Pastille de score Performance : score brut /100 (pas de %), seuils Performance.
function ScorePill({ value }) {
  return (
    <SharedScorePill
      value={value}
      suffix=""
      warnBelow={90}
      dangerBelow={50}
      perfectAt={95}
      hideZero
      minWidthClass="min-w-[36px]"
    />
  );
}

// ── Détail par dimension PageSpeed ───────────────────────────────────────────

function DimensionBreakdown({ audits }) {
  const valid = audits.filter(
    (a) => Number(a.pagespeed_average) > 0 && !a.loading && !a.error
  );
  const n = valid.length;
  if (!n) return null;

  const avg = (key) => {
    const vals = valid.map((a) => Number(a[key] || 0)).filter((v) => v > 0);
    return vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : null;
  };

  const dims = [
    { key: 'pagespeed_performance_desktop', label: 'Desktop' },
    { key: 'pagespeed_performance_mobile', label: 'Mobile' },
    { key: 'pagespeed_accessibility', label: 'Accessibilité' },
    { key: 'pagespeed_best_practices', label: 'Bonnes pratiques' },
    { key: 'pagespeed_seo', label: 'SEO Lighthouse' },
  ];

  return (
    <div className="mt-4 pt-4 border-t border-blue-gray-100">
      <p className="text-[11px] font-semibold text-blue-gray-400 uppercase tracking-wide mb-3">
        Détail par dimension · {n} page{n > 1 ? 's' : ''} analysée{n > 1 ? 's' : ''}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
        {dims.map(({ key, label }) => {
          const score = avg(key);
          if (score === null) return null;
          const good = score >= 90;
          const warn = score >= 50;
          const barColor = good ? 'bg-green-500' : warn ? 'bg-amber-400' : 'bg-red-400';
          const textColor = good ? 'text-green-600' : warn ? 'text-amber-600' : 'text-red-500';
          return (
            <div key={key} className="flex items-center gap-3">
              <span className="text-xs text-blue-gray-600 w-32 shrink-0">{label}</span>
              <div className="flex-1 bg-blue-gray-100 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all duration-500 ${barColor}`}
                  style={{ width: `${score}%` }}
                />
              </div>
              <span className={`text-xs font-bold w-7 text-right ${textColor}`}>{score}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CHECKBOX_CLASS = "w-4 h-4 text-accent bg-white border-2 border-blue-gray-300 rounded focus:ring-2 focus:ring-accent/50 focus:ring-offset-1 cursor-pointer transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed";

function getCheckboxStyle(checked, indeterminate) {
  const img = checked && !indeterminate
    ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M2 6l3 3 5-6'/%3E%3C/svg%3E\")"
    : indeterminate
      ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='none' stroke='white' stroke-width='2' stroke-linecap='round' d='M2 6h8'/%3E%3C/svg%3E\")"
      : 'none';
  return {
    appearance: 'none', WebkitAppearance: 'none',
    backgroundImage: img,
    backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
    backgroundColor: (checked || indeterminate) ? 'var(--color-accent, #6366f1)' : undefined,
    borderColor: (checked || indeterminate) ? 'var(--color-accent, #6366f1)' : undefined,
  };
}

function urlToPageSlug(url) {
  try {
    const u = new URL(url);
    let path = u.pathname.replace(/^\/+|\/+$/g, '');
    return path ? path.replace(/\//g, '___') : 'home';
  } catch {
    return encodeURIComponent(url);
  }
}

export default function ProjectPerformance() {
  const { project } = useOutletContext();
  const navigate = useNavigate();
  const { slug } = useParams();

  const {
    audits, loading, error, sort,
    isAuditing, currentUrlsBatch, progress,
    rowLoading, pagespeedKeyConfigured, selectedUrls,
    setSort,
    handleSort, handleAnalyseOne, handleAudit,
    getSortedAudits, toggleUrlSelection, selectAllUrls
  } = usePerformanceAudits(
    project?.slug,
    project?.url,
    project?.reference_pages,
    project?.date_dernier_audit,
  );

  const sortedAudits = getSortedAudits();
  const refPages = getReferencePagesList(project);
  const [referenceOnly, setReferenceOnly] = useState(refPages.length > 0);

  const tableAudits = useMemo(() => {
    let rows = sortedAudits;
    if (referenceOnly) {
      rows = rows.filter((a) => isReferencePage(a.url, project));
    }
    return sortRowsWithReferenceFirst(rows, project);
  }, [sortedAudits, referenceOnly, project]);

  const validPerformanceAudits = sortedAudits.filter((a) => {
    if (a.pagespeed_average == null || a.pagespeed_average <= 0 || a.loading || a.error) return false;
    if (refPages.length > 0 && !isReferencePage(a.url, project)) return false;
    return true;
  });

  const { performance: averagePerformanceScore } = useProjectScores(project, { perfRows: audits });

  const getPerformanceScoreColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 50) return 'warning';
    return 'error';
  };

  const isAllSelected = audits.length > 0 && selectedUrls.size === audits.filter(a => a.url).length;
  const isIndeterminate = selectedUrls.size > 0 && selectedUrls.size < audits.filter(a => a.url).length;

  const toggleSelectAll = () => selectAllUrls(audits.filter(a => a.url));

  const handleAuditSelected = async () => {
    if (selectedUrls.size === 0) return;
    await handleAudit(Array.from(selectedUrls));
  };

  function handleRowClick(audit) {
    if (audit.loading || audit.error || !audit.pagespeed_average) return;
    const pageSlug = urlToPageSlug(audit.url);
    navigate(`/projects/${project.slug}/seo/${pageSlug}?tab=performance`);
  }

  const selectHeaderCheckboxRef = React.useRef(null);
  React.useEffect(() => {
    if (selectHeaderCheckboxRef.current) selectHeaderCheckboxRef.current.indeterminate = isIndeterminate;
  }, [isIndeterminate]);

  const renderSelectHeader = () => (
    <div onClick={(e) => { e.stopPropagation(); toggleSelectAll(); }} className="flex items-center justify-center">
      <input
        ref={selectHeaderCheckboxRef}
        type="checkbox"
        checked={isAllSelected && !isIndeterminate}
        onChange={(e) => { e.stopPropagation(); toggleSelectAll(); }}
        onClick={(e) => e.stopPropagation()}
        disabled={isAuditing}
        className={CHECKBOX_CLASS}
        style={getCheckboxStyle(isAllSelected && !isIndeterminate, isIndeterminate)}
      />
    </div>
  );

  const columns = [
    { label: '', key: 'select', align: 'center', className: 'w-8', renderHeader: renderSelectHeader },
    { label: 'URL', key: 'url', align: 'left', className: 'w-[32%] min-w-[180px]' },
    { label: 'Score', key: 'pagespeed_average', align: 'center', className: 'w-[56px]' },
    { label: 'Desktop', key: 'pagespeed_performance_desktop', align: 'center', className: 'w-[56px]' },
    { label: 'Mobile', key: 'pagespeed_performance_mobile', align: 'center', className: 'w-[56px]' },
    { label: 'Accessib.', key: 'pagespeed_accessibility', align: 'center', className: 'w-[64px]' },
    { label: 'B. Pratiques', key: 'pagespeed_best_practices', align: 'center', className: 'w-[72px]' },
    { label: 'SEO LH', key: 'pagespeed_seo', align: 'center', className: 'w-[56px]' },
    { label: '', key: 'detail', align: 'right', className: 'w-[100px]' },
  ];

  function renderCell(audit, col) {
    const hasPageSpeed = audit.pagespeed_average != null && audit.pagespeed_average > 0;
    const hasAudit = audit.id || audit.date_audit || audit.title !== undefined || audit.score_global !== undefined;
    const isCurrentUrlBeingAudited = isAuditing && currentUrlsBatch.includes(audit.url);
    const isUrlInQueue = isAuditing && !hasPageSpeed && !audit.loading && !audit.error && !isCurrentUrlBeingAudited;

    if (col.key === 'select') {
      const isSelected = selectedUrls.has(audit.url);
      return (
        <div onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => { e.stopPropagation(); toggleUrlSelection(audit.url); }}
            onClick={(e) => e.stopPropagation()}
            disabled={isAuditing || audit.loading}
            className={CHECKBOX_CLASS}
            style={getCheckboxStyle(isSelected, false)}
          />
        </div>
      );
    }

    if ((audit.loading || isCurrentUrlBeingAudited) && !hasPageSpeed) {
      if (col.key === 'url') {
        return (
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-blue-gray-800 text-sm truncate">{audit.url.replace(/^https?:\/\//, '')}</span>
            <span className="inline-flex items-center gap-1 text-accent text-xs flex-shrink-0">
              <span className="w-1 h-1 bg-accent rounded-full animate-pulse" />
              Analyse...
            </span>
          </span>
        );
      }
      if (col.key === 'detail') return <span className="text-accent text-xs">Analyse...</span>;
      return <span className="flex justify-center"><span className="w-2.5 h-2.5 border border-accent/30 border-t-accent rounded-full animate-spin" /></span>;
    }

    if (isUrlInQueue) {
      if (col.key === 'url') {
        return (
          <span className="flex items-center gap-2 opacity-60 min-w-0">
            <span className="text-blue-gray-600 text-sm truncate">{audit.url.replace(/^https?:\/\//, '')}</span>
            <span className="text-xs text-blue-gray-400 flex-shrink-0">En attente</span>
          </span>
        );
      }
      return <span className="text-blue-gray-300 block text-center">—</span>;
    }

    if (audit.pagespeed_error) {
      if (col.key === 'url') {
        return (
          <Tooltip content={audit.pagespeed_error}>
            <span className="flex items-center gap-2 min-w-0">
              <span className="text-red-600 text-sm truncate">{audit.url.replace(/^https?:\/\//, '')}</span>
              <XCircle size={14} className="text-red-400 flex-shrink-0" />
            </span>
          </Tooltip>
        );
      }
      if (col.key === 'pagespeed_average') return <span className="text-red-400 text-xs">Erreur</span>;
      if (col.key === 'detail') {
        return (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAnalyseOne(audit.url); }}
            disabled={isAuditing || loading}
            className="text-sm text-blue-gray-500 hover:text-accent hover:bg-blue-gray-100 rounded px-1.5 py-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Réessayer
          </button>
        );
      }
      return null;
    }

    if (col.key === 'url') {
      const isRef = isReferencePage(audit.url, project);
      return (
        <span className="flex items-center gap-2 min-w-0">
          <a
            href={audit.url}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline text-blue-gray-800 text-sm truncate font-normal"
            title={audit.url}
            onClick={(e) => e.stopPropagation()}
          >
            {audit.url.replace(/^https?:\/\//, '')}
          </a>
          {isRef && <ReferencePageBadge compact />}
          {hasPageSpeed
            ? <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0" title="Analysé" />
            : hasAudit
              ? <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" title="Audit sans PageSpeed" />
              : <span className="w-1.5 h-1.5 bg-blue-gray-300 rounded-full flex-shrink-0" title="Non analysé" />
          }
        </span>
      );
    }

    if (col.key.startsWith('pagespeed_')) {
      if (!hasPageSpeed) return <span className="text-blue-gray-300 block text-center">—</span>;
      const score = audit[col.key];
      if (score == null) return null;
      if (col.key === 'pagespeed_average') {
        return (
          <Tooltip content={
            <div className="text-sm space-y-0.5">
              <div>Desktop : {Math.round(audit.pagespeed_performance_desktop || 0)}</div>
              <div>Mobile : {Math.round(audit.pagespeed_performance_mobile || 0)}</div>
              <div>Access. : {Math.round(audit.pagespeed_accessibility || 0)}</div>
              <div>B.P. : {Math.round(audit.pagespeed_best_practices || 0)}</div>
              <div>SEO : {Math.round(audit.pagespeed_seo || 0)}</div>
            </div>
          }>
            <div className="flex justify-center"><ScorePill value={score} /></div>
          </Tooltip>
        );
      }
      return <div className="flex justify-center"><ScorePill value={score} /></div>;
    }

    if (col.key === 'detail') {
      if (!audit.url) return null;
      if (rowLoading[audit.url]) return <span className="text-accent text-xs">Analyse...</span>;
      const isDisabled = rowLoading[audit.url] || isAuditing || loading || !audit.url;
      return (
        <div className="flex items-center gap-1 justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault(); e.stopPropagation();
              if (!isDisabled && audit.url) handleAnalyseOne(audit.url);
            }}
            disabled={isDisabled}
            className="text-sm text-blue-gray-500 hover:text-accent hover:bg-blue-gray-100 rounded px-1.5 py-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {hasPageSpeed ? 'Réanalyser' : 'Analyser'}
          </button>
          {hasPageSpeed && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault(); e.stopPropagation();
                const pageSlug = urlToPageSlug(audit.url);
                navigate(`/projects/${project.slug}/seo/${pageSlug}?tab=performance`);
              }}
              title="Voir le détail"
              className="p-1 rounded text-blue-gray-400 hover:text-accent hover:bg-blue-gray-100 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      );
    }
    return null;
  }

  if (!project) return <Loader label="Chargement du projet..." />;

  const noPerformanceData = !loading && validPerformanceAudits.length === 0;

  return (
    <div className="w-full mx-auto px-2 md:px-0 flex flex-col gap-6">
      <ReferencePagesBanner project={project} />
      {refPages.length === 0 && (
        <AuditEmptyState
          title="Pages de référence requises"
          description="Définissez vos pages stratégiques pour lancer PageSpeed. Le score performance du projet est calculé uniquement sur ces pages."
          actionLabel="Configurer les pages de référence"
          onAction={() => navigate(`/projects/${slug}/reference-pages`)}
        />
      )}
      {refPages.length > 0 && noPerformanceData && (
        <AuditEmptyState
          title="Aucune donnée PageSpeed"
          description="Lancez l'analyse sur vos pages de référence (bouton ci-dessous ou via l'audit complet, étape Performance)."
        />
      )}

      {validPerformanceAudits.length > 0 && (
        <Card className="w-full">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-start gap-2 mb-1">
                <h2 className="font-heading text-base font-semibold text-blue-gray-900">Score Performance moyen</h2>
                <Tooltip content={
                  <div className="text-sm">
                    <div className="font-semibold mb-2">Calcul du score Performance</div>
                    <div className="space-y-1 text-neutral-200">
                      <div>• <strong>Desktop + Mobile</strong> : 70% du score (35% chacun)</div>
                      <div>• <strong>Accessibilité</strong> : 10%</div>
                      <div>• <strong>Bonnes pratiques</strong> : 10%</div>
                      <div>• <strong>SEO Lighthouse</strong> : 10%</div>
                    </div>
                  </div>
                }>
                  <Info size={15} className="text-blue-gray-400 hover:text-blue-gray-600 cursor-help mt-0.5 flex-shrink-0" />
                </Tooltip>
              </div>
              {refPages.length > 0 && (
                <p className="text-xs text-blue-gray-500">Moyenne des pages de référence uniquement</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-accent">{averagePerformanceScore}</span>
                <span className="text-base text-blue-gray-400">/100</span>
              </div>
              <Badge color={getPerformanceScoreColor(averagePerformanceScore)} className="text-sm">
                {getScoreLabelText(averagePerformanceScore)}
              </Badge>
            </div>
          </div>
          <DimensionBreakdown audits={sortedAudits} />
        </Card>
      )}

      {refPages.length > 0 && (
      <Card className="w-full">
        <div className="w-full flex justify-between items-center mb-6">
          <h3 className="font-heading text-lg font-semibold text-blue-gray-900">Performance des pages de référence</h3>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {(project?.reference_pages?.length > 0) && (
              <label className="inline-flex items-center gap-2 text-xs text-blue-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={referenceOnly}
                  onChange={(e) => setReferenceOnly(e.target.checked)}
                  className="w-3.5 h-3.5 text-accent rounded border-blue-gray-300"
                />
                Réf. seulement
              </label>
            )}
            {selectedUrls.size > 0 && (
              <span className="text-blue-gray-600 text-sm font-medium">
                {selectedUrls.size} page{selectedUrls.size > 1 ? 's' : ''} sélectionnée{selectedUrls.size > 1 ? 's' : ''}
              </span>
            )}
            {selectedUrls.size > 0 && (
              <Button variant="primary" size="sm" onClick={handleAuditSelected} disabled={isAuditing || loading}>
                {isAuditing ? 'Analyse en cours...' : `Analyser les ${selectedUrls.size} page${selectedUrls.size > 1 ? 's' : ''}`}
              </Button>
            )}
          </div>
        </div>

        {isAuditing && (
          <div className="mb-6 p-4 rounded-xl bg-accent/5 border border-accent/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                <span className="text-blue-gray-900 font-semibold text-sm">Audit de performance en cours...</span>
              </div>
              <span className="text-accent font-bold">{progress}%</span>
            </div>
            {currentUrlsBatch.length > 0 && (
              <div className="text-blue-gray-500 font-mono text-xs truncate mb-2">
                {currentUrlsBatch.length} page{currentUrlsBatch.length > 1 ? 's' : ''} en cours : {currentUrlsBatch[0].replace(/^https?:\/\//, '')}
                {currentUrlsBatch.length > 1 && ` (+${currentUrlsBatch.length - 1})`}
              </div>
            )}
            <div className="w-full h-1.5 bg-blue-gray-100 rounded-full overflow-hidden">
              <div className="h-1.5 bg-accent rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {loading ? (
          <Loader label="Chargement des audits de performance..." />
        ) : error ? (
          <div className="flex flex-col items-center justify-center text-center py-12 space-y-4">
            <AlertTriangle className="w-10 h-10 text-red-400" />
            <div>
              <p className="text-sm font-medium text-red-600 mb-1">Erreur lors du chargement</p>
              <p className="text-sm text-blue-gray-500 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()} variant="primary" size="sm">Réessayer</Button>
            </div>
          </div>
        ) : !audits.length ? (
          <div className="text-blue-gray-400 text-sm py-8 text-center">
            Aucun audit de performance disponible. Lancez un audit ou configurez la clé Google PageSpeed.
          </div>
        ) : (
          <>
            {!pagespeedKeyConfigured && audits.some(a => (a.id || a.date_audit) && !a.pagespeed_average) && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="text-sm text-orange-800">
                  <strong>Attention :</strong> Certaines URLs n'ont pas de scores. Configurez la clé API PageSpeed dans les paramètres du projet.
                </div>
              </div>
            )}
            <Table
              columns={columns}
              rows={tableAudits}
              caption="Tableau d'analyse de performance des URLs du projet"
              renderCell={renderCell}
              sort={sort}
              onSort={handleSort}
              onRowClick={handleRowClick}
              getRowClassName={(row) => (isReferencePage(row.url, project) ? REFERENCE_ROW_CLASS : '')}
            />
          </>
        )}
      </Card>
      )}
    </div>
  );
}
